import React, { useState, useEffect } from 'react';
import { useClient } from '../ClientContext';
import { apiGet, apiPost, apiPatch, AuthError } from '../api';
import './reporting/reporting.css';

// BidSense's Business Manager. The client adds this as a partner on their ad
// account, which grants access without needing Advanced permissions on the app.
const BIDSENSE_BUSINESS_ID = '968478766203474';

const STATUS_PILL = {
  1: 'approved',
  2: 'blocked',
  3: 'flagged',
  7: 'flagged',
  8: 'flagged',
  9: 'flagged',
  101: 'blocked',
};

function Section({ title, sub, children, action }) {
  return (
    <div className="card set-section">
      <div className="card-header">
        <div>
          <span className="card-title">{title}</span>
          {sub && <div className="set-sub">{sub}</div>}
        </div>
        {action}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}


/**
 * How to grant BidSense access.
 *
 * Written out in full rather than hidden behind a help link, because this is
 * the step that happens in someone else's interface and is the most likely
 * place an onboarding stalls. Someone who cannot complete it will not open a
 * support ticket — they will lose interest.
 */
function PartnerInstructions() {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(BIDSENSE_BUSINESS_ID);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <p className="set-intro">
        BidSense needs partner access to your ad account. You grant it from your own Business
        Manager, and you can withdraw it there at any time — BidSense never holds your password
        and cannot see anything you have not shared.
      </p>

      <ol className="set-ol">
        <li>
          Open <strong>Meta Business Manager</strong> → Settings → Ad accounts, and select the
          account you want to connect.
        </li>
        <li>
          Choose <strong>Assign partner</strong>, then <strong>Business ID</strong>.
        </li>
        <li>
          Enter BidSense's business ID:
          <span className="set-copy">
            <span className="tag-mono">{BIDSENSE_BUSINESS_ID}</span>
            <button className="btn btn-secondary set-copy-btn" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </span>
        </li>
        <li>
          Grant <strong>Manage campaigns</strong> permission. BidSense needs to create campaigns,
          not only read them.
        </li>
        <li>
          Come back here and enter your ad account id below. We will check the connection works
          before saving it.
        </li>
      </ol>
    </div>
  );
}


function ConnectAccount({ clients, onConnected }) {
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState(clients[0]?.client_id || '');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verified, setVerified] = useState(null);
  const [error, setError] = useState(null);

  const verify = async () => {
    setChecking(true);
    setError(null);
    setVerified(null);
    try {
      const account = await apiPost('/settings/connections/verify', {
        platform_account_id: accountId.trim(),
      });
      setVerified(account);
    } catch (err) {
      setError(err.message || 'Could not check that account.');
    }
    setChecking(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await apiPost('/settings/connections', {
        client_id: clientId,
        platform_account_id: accountId.trim(),
      });
      setAccountId('');
      setVerified(null);
      onConnected(result);
    } catch (err) {
      setError(err.message || 'Could not save that connection.');
    }
    setSaving(false);
  };

  return (
    <>
      <PartnerInstructions />

      <div className="set-connect">
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label className="form-label">Ad account ID</label>
          <input
            className="form-input mono"
            value={accountId}
            onChange={(e) => { setAccountId(e.target.value); setVerified(null); }}
            placeholder="act_1234567890123456"
          />
        </div>
        <button
          className="btn btn-secondary"
          onClick={verify}
          disabled={checking || !accountId.trim()}
        >
          {checking ? 'Checking…' : 'Check connection'}
        </button>
      </div>

      {error && <div className="set-error">{error}</div>}

      {verified && (
        <div className="set-verified">
          <div className="set-verified-head">
            <span className={`pill ${STATUS_PILL[verified.status] || 'observe'}`}>
              {verified.status_label}
            </span>
            <strong>{verified.name}</strong>
            <span className="tag-mono">{verified.account_id}</span>
          </div>

          <div className="set-verified-meta">
            {verified.currency && <span>Currency {verified.currency}</span>}
            {verified.timezone && <span>{verified.timezone}</span>}
          </div>

          {verified.already_connected_to && (
            <div className="set-warn">
              This account is already connected to {verified.already_connected_to}.
            </div>
          )}

          {/* Meta will accept a campaign against a disabled account and then
              fail to deliver it. Better said now than discovered later. */}
          {!verified.usable && !verified.already_connected_to && (
            <div className="set-warn">
              This account is not active on Meta, so campaigns created against it will not
              deliver. You can still connect it and resolve the account status separately.
            </div>
          )}

          {!verified.already_connected_to && (
            <div className="set-assign">
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Connect to client</label>
                <select
                  className="form-select"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-brass" onClick={save} disabled={saving || !clientId}>
                {saving ? 'Connecting…' : 'Connect account'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}


function AddClient({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiPost('/settings/clients', { name: name.trim() });
      setName('');
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err.message || 'Could not add that client.');
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button className="btn btn-secondary" onClick={() => setOpen(true)}>
        + Add client
      </button>
    );
  }

  return (
    <div className="set-addclient">
      <input
        className="form-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Client name"
        maxLength={120}
        autoFocus
      />
      <button className="btn btn-brass" onClick={save} disabled={saving || !name.trim()}>
        {saving ? 'Adding…' : 'Add'}
      </button>
      <button className="btn btn-secondary" onClick={() => { setOpen(false); setError(null); }}>
        Cancel
      </button>
      {error && <div className="set-error">{error}</div>}
    </div>
  );
}


export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = () => {
    Promise.all([apiGet('/settings/profile'), apiGet('/settings/connections')])
      .then(([p, c]) => {
        setProfile(p);
        setConnections(c.connections || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : (err.message || 'Could not load settings.'));
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, []);

  const disconnect = async (id) => {
    setError(null);
    try {
      await apiPatch(`/settings/connections/${id}/disconnect`);
      load();
    } catch (err) {
      setError(err.message || 'Could not disconnect that account.');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: 'var(--grey)', fontSize: 14 }}>Loading settings…</div>
    </div>
  );

  const canManage = profile && ['owner', 'admin'].includes(profile.role);
  const active = connections.filter((c) => c.is_active);
  const inactive = connections.filter((c) => !c.is_active);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Your profile, your clients, and the ad accounts BidSense can act on</p>
      </div>

      {error && <div className="set-error set-error-top">{error}</div>}
      {notice && <div className="set-notice">{notice}</div>}

      <Section title="Profile">
        <div className="set-kv">
          <div><span className="set-k">Signed in as</span>{profile.email}</div>
          <div><span className="set-k">Organisation</span>{profile.agency_name || '—'}</div>
          <div>
            <span className="set-k">Role</span>
            <span className="pill observe">{profile.role}</span>
          </div>
        </div>
      </Section>

      <Section
        title="Clients"
        sub={`${profile.clients.length} client${profile.clients.length === 1 ? '' : 's'} you can access`}
        action={canManage ? <AddClient onAdded={load} /> : null}
      >
        {profile.clients.length === 0 ? (
          <p className="set-empty">No clients yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Client</th><th>Connected accounts</th></tr>
            </thead>
            <tbody>
              {profile.clients.map((c) => (
                <tr key={c.client_id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td>
                    {c.accounts?.length
                      ? c.accounts.map((a) => <span key={a} className="tag-mono set-acc">{a}</span>)
                      : <span className="set-none">Not connected</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Connected ad accounts"
        sub="Accounts BidSense can read and build campaigns against"
      >
        {active.length === 0 ? (
          <p className="set-empty">
            No ad accounts connected yet. Follow the steps below to grant access.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Account</th><th>Client</th><th>Platform</th><th>Connected</th><th></th></tr>
            </thead>
            <tbody>
              {active.map((c) => (
                <tr key={c.connection_id}>
                  <td className="mono">{c.platform_account_id}</td>
                  <td>{c.client_name}</td>
                  <td><span className="pill approved">Meta</span></td>
                  <td style={{ fontSize: 12, color: 'var(--grey)' }}>
                    {new Date(c.connected_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td>
                    {canManage && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '5px 11px' }}
                        onClick={() => disconnect(c.connection_id)}
                      >
                        Disconnect
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {inactive.length > 0 && (
          <p className="set-none" style={{ marginTop: 'var(--space-4)' }}>
            {inactive.length} previously connected account{inactive.length === 1 ? '' : 's'} kept
            for audit history.
          </p>
        )}
      </Section>

      {canManage && (
        <Section
          title="Connect an ad account"
          sub="Meta — partner access from your Business Manager"
        >
          <ConnectAccount
            clients={profile.clients}
            onConnected={(r) => {
              setNotice(r.message);
              load();
              setTimeout(() => setNotice(null), 8000);
            }}
          />
        </Section>
      )}

      {!canManage && (
        <p className="set-none">
          Connecting and disconnecting ad accounts is limited to owners and administrators.
        </p>
      )}
    </>
  );
}